// Shared invoice display helpers used across the admin billing UI
// (AgencyProfileView, SuperAdminPayments) and the public invoice page.
// Centralized so the "INV-INV-" doubling and the overdue-classification rules
// stay consistent in every place an invoice is rendered.

export interface InvoiceLike {
  status?: string | null;
  prefix?: string | null;
  invoice_number?: string | null;
  due_date?: string | null;
  is_recurring?: boolean | null;
}

/** Today as YYYY-MM-DD in Guatemala business time (UTC-6, no DST). */
export function todayGtYmd(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' });
}

/**
 * p_paid_at for a YYYY-MM-DD picked in an admin "mark paid" form.
 *
 * Noon local keeps the calendar day identical in UTC (the RPC casts
 * p_paid_at::date). But for TODAY, noon can still be in the future — and the
 * paid instant becomes the add-on's purchase_date, which get_agency_caps
 * ignores until it has started. Marked before 12:00 GT, the seats just paid
 * for stayed locked until noon (CORPOraices, 2026-09-14). Clamping to now is
 * safe: any instant before noon GT falls on the same UTC date.
 */
export function paidAtIso(ymd: string, now: number = Date.now()): string {
  return new Date(Math.min(new Date(`${ymd}T12:00:00`).getTime(), now)).toISOString();
}

/**
 * Format a "paid-through"/due DATE for display without a timezone shift.
 *
 * next_payment_date / due_date are stored as UTC-midnight timestamptz
 * ("2026-08-01T00:00:00+00:00"). `new Date(d).toLocaleDateString('es-GT')`
 * renders that in UTC-6 as the PREVIOUS day (Jul 31). These are calendar
 * dates, not instants — take the YYYY-MM-DD and format it as a local date so
 * the day never slips. Accepts bare 'YYYY-MM-DD' or full ISO timestamps.
 */
export function formatBillingDate(
  d: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' },
): string {
  if (!d) return '—';
  const [y, m, day] = d.slice(0, 10).split('-').map(Number);
  if (!y || !m || !day) return '—';
  return new Date(y, m - 1, day).toLocaleDateString('es-GT', opts);
}

/** Spanish label for a billing-period value (legacy rows store English). */
export function cycleLabelEs(period: string | null | undefined): string {
  const c = (period || '').toLowerCase();
  if (['mensual', 'monthly', 'month'].includes(c)) return 'mensual';
  if (['semestral', 'semiannual', 'biannual', 'biannually'].includes(c)) return 'semestral';
  if (['anual', 'annual', 'yearly', 'year'].includes(c)) return 'anual';
  return period || '';
}

/** Months per billing cycle, mirroring the server's _cycle_text_to_interval. */
export function cycleMonths(cycle: string | null | undefined): number {
  const c = (cycle || '').toLowerCase();
  if (['semestral', 'semiannual', 'biannual', 'biannually'].includes(c)) return 6;
  if (['anual', 'annual', 'yearly', 'year'].includes(c)) return 12;
  return 1;
}

/**
 * Cycle end / next-payment date for a cycle starting at `startYmd`
 * ("YYYY-MM-DD" or full ISO), using the same calendar-month arithmetic as the
 * server's rotation (_potential_next_payment: start + interval '1 month' /
 * '6 months' / '1 year'). NOT fixed 30/180/365 days — a monthly cycle starting
 * Jul 13 ends Aug 13, not Aug 12. Postgres clamps month overflow to the last
 * day of the target month (Jan 31 + 1 month = Feb 28), so clamp the same way.
 * Returned Date is anchored at local noon so formatting never shifts a day.
 */
export function addCycle(startYmd: string, cycle: string | null | undefined): Date | null {
  const [y, m, d] = startYmd.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const months = cycleMonths(cycle);
  const target = new Date(y, m - 1 + months, 1, 12);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d, lastDay));
  return target;
}

/**
 * Render an invoice number without doubling the prefix.
 *
 * Invoice numbers come in two schemes in this DB:
 *  - V2 RPC invoices bake the prefix into invoice_number ("INV-20260608-013223").
 *  - Legacy/admin invoices store a bare number ("1000") that NEEDS the prefix.
 *
 * Prepend the prefix only when invoice_number doesn't already start with it —
 * this fixes the "INV-INV-..." doubling on the ~256 prefixed invoices without
 * dropping the prefix from the ~7.5k bare-number invoices.
 */
export function formatInvoiceNumber(inv: InvoiceLike | null | undefined): string {
  const num = String(inv?.invoice_number ?? '');
  const prefix = inv?.prefix ?? '';
  if (!prefix) return num;
  return num.startsWith(prefix) ? num : `${prefix}${num}`;
}

/**
 * Whether an invoice should display as overdue ("Vencida").
 *  - status 'overdue' (cron-set) → always overdue.
 *  - Non-recurring one-off invoices (corrections / add-on catch-ups) are never
 *    tied to next_payment_date, so no cron flips them: derive overdue straight
 *    from due_date once it passes (these are due on issue — no grace window).
 *  - Recurring invoices honor the deliberate 5-day grace period: trust the
 *    server status only (run_payment_overdue flips them after grace), so an
 *    in-grace membership invoice is NOT prematurely shown as Vencida.
 */
export function isInvoiceOverdue(inv: InvoiceLike | null | undefined): boolean {
  if (!inv) return false;
  if (inv.status === 'overdue') return true;
  if (inv.status !== 'sent' && inv.status !== 'draft') return false;
  if (inv.is_recurring) return false;
  const due = inv.due_date ? String(inv.due_date).slice(0, 10) : '';
  return !!due && due < todayGtYmd();
}

/**
 * One-off correction invoices (add-on under-billing catch-ups, manual
 * remediations) carry a `source: 'remediation'` / `flow: 'catchup'` marker in
 * metadata. Surfacing a "Corrección" tag makes these self-explanatory — they're
 * intentionally unrelated to the agency's regular billing cycle.
 */
export function isCorrectionInvoice(inv: { metadata?: any } | null | undefined): boolean {
  const m = inv?.metadata;
  if (!m) return false;
  return m.source === 'remediation' || m.flow === 'catchup';
}

/**
 * Payment trust class. Webhook/gateway-settled money always carries a
 * gateway_tx_id (UNIQUE-constrained); an admin mark-paid sets recorded_by
 * instead. The distinction matters: the 2026-07-13 audit found manual marks
 * with no matching gateway money, so the UI must not render both identically.
 */
export function paymentVerification(
  p: { gateway_tx_id?: string | null; recorded_by?: string | null; metadata?: any } | null | undefined,
): 'verified' | 'manual' | null {
  if (!p) return null;
  if (p.gateway_tx_id) return 'verified';
  if (p.recorded_by || p.metadata?.recorded_by) return 'manual';
  return null;
}

/**
 * Coarse "what was this payment for" badge, derived from payment metadata and
 * the linked package's category. Returns a Spanish label + tailwind classes
 * so every admin surface renders the same chip.
 */
export function paymentTypeBadge(
  p: { metadata?: any; saas_packages?: { category?: string | null } | null } | null | undefined,
): { label: string; cls: string } | null {
  if (!p) return null;
  const meta = p.metadata || {};
  if (meta.flow === 'catchup' || meta.source === 'remediation') {
    return { label: 'Corrección', cls: 'bg-violet-100 text-violet-700' };
  }
  if (meta.source === 'recurring_renewal') {
    return { label: 'Renovación auto', cls: 'bg-teal-100 text-teal-700' };
  }
  const cat = p.saas_packages?.category;
  if (cat === 'membership') return { label: 'Membresía', cls: 'bg-indigo-100 text-indigo-700' };
  if (cat === 'featured' || cat === 'featured_pro') return { label: 'Destaque', cls: 'bg-amber-100 text-amber-700' };
  if (cat) return { label: 'Complemento', cls: 'bg-sky-100 text-sky-700' };
  return null;
}
